import React, { useState } from 'react';

// Fault type definitions
const FAULT_TYPES = [
  {
    id: 'SLG',
    label: 'Single-Line-to-Ground (SLG)',
    short: 'SLG',
    color: '#7c3aed',
    boundary: 'Va = 0,  Ib = Ic = 0  (Phase A grounded)',
    connection: 'Z\u2081, Z\u2082, Z\u2080 in series',
    formula: 'I\u2081 = I\u2082 = I\u2080 = Vf / (Z\u2081 + Z\u2082 + Z\u2080)',
    faultCurrent: 'If = 3I\u2081 = 3Vf / (Z\u2081 + Z\u2082 + Z\u2080)',
    keyFact: 'Equal sequence currents: |I\u2081| = |I\u2082| = |I\u2080|. Three-sequence fingerprint of SLG fault. Zero-sequence network must be closed for fault current to flow.',
    seqDiagram: 'series',
  },
  {
    id: 'LL',
    label: 'Line-to-Line (LL)',
    short: 'LL',
    color: '#ea580c',
    boundary: 'Ia = 0,  Vb = Vc  (Phase B-C short)',
    connection: 'Z\u2081 and Z\u2082 in series; Z\u2080 not involved',
    formula: 'I\u2081 = Vf / (Z\u2081 + Z\u2082),  I\u2082 = \u2212I\u2081,  I\u2080 = 0',
    faultCurrent: '|If| = \u221a3 |I\u2081| (line current magnitude)',
    keyFact: 'No zero-sequence current \u2014 LL faults cannot be detected by zero-sequence (ground) relays (51G). Only positive and negative sequence elements see this fault.',
    seqDiagram: 'pos-neg-series',
  },
  {
    id: 'DLG',
    label: 'Double-Line-to-Ground (DLG)',
    short: 'DLG',
    color: '#0d9488',
    boundary: 'Ia = 0,  Vb = Vc = 0  (Phase B and C grounded)',
    connection: 'Z\u2082 \u2225 Z\u2080 in parallel, then Z\u2081 in series',
    formula: 'I\u2081 = Vf / (Z\u2081 + Z\u2082\u2225Z\u2080)',
    faultCurrent: 'Ia_fault = 3I\u2080,  Ib \u2260 Ic (unequal)',
    keyFact: 'Highest fault current for most systems (often exceeds SLG). Zero-sequence current flows only if a ground path exists. The parallel Z\u2082\u2225Z\u2080 means small Z\u2080 can significantly increase fault current.',
    seqDiagram: 'pos-neg-zero-parallel',
  },
  {
    id: '3PH',
    label: 'Three-Phase (Balanced)',
    short: '3PH',
    color: '#0d9488',
    boundary: 'Va = Vb = Vc = 0  (All three phases shorted)',
    connection: 'Only Z\u2081 — Z\u2082 and Z\u2080 not connected',
    formula: 'I\u2081 = Vf / Z\u2081,  I\u2082 = I\u2080 = 0',
    faultCurrent: 'If = Vf / Z\u2081 (per phase, 120\u00b0 apart)',
    keyFact: 'Only positive sequence current flows \u2014 balanced fault. Maximum fault current for strong systems (Z\u2080 is not in series). The reference case for relay short-circuit ratings.',
    seqDiagram: 'pos-only',
  },
];

// ─── SVG: Sequence network connection diagram ─────────────────────────────────
const ConnectionDiagram = ({ faultType }) => {
  const { seqDiagram } = faultType;

  const POS_C = '#0d9488', NEG_C = '#ea580c', ZER_C = '#7c3aed', DIM_C = '#94a3b8';
  const W = 320, H = 130;

  const Box = ({ x, y, w = 70, h = 22, label, c, dim = false }) => (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={3}
        fill={dim ? 'rgba(148,163,184,0.08)' : 'rgba(148,163,184,0.15)'}
        stroke={dim ? DIM_C : c} strokeWidth={1.5} strokeDasharray={dim ? '3 2' : undefined} />
      <text x={x + w / 2} y={y + h / 2 + 4} textAnchor="middle"
        fontSize="9" fontWeight="700" fontFamily="IBM Plex Mono, monospace"
        fill={dim ? DIM_C : c}>{label}</text>
    </g>
  );

  const FaultBus = ({ x, y }) => (
    <g>
      <line x1={x - 20} y1={y} x2={x + 20} y2={y} stroke="#1e293b" strokeWidth={3} strokeLinecap="round" />
      <text x={x} y={y - 6} textAnchor="middle" fontSize="8" fill="#64748b" fontWeight="600">F.P.</text>
    </g>
  );

  if (seqDiagram === 'pos-only') {
    // 3PH: only positive sequence
    return (
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: '130px' }}>
        <FaultBus x={160} y={20} />
        <line x1={160} y1={20} x2={160} y2={40} stroke={POS_C} strokeWidth={1.8} />
        <Box x={125} y={40} label="Z\u2081_Th" c={POS_C} />
        <line x1={160} y1={62} x2={160} y2={78} stroke={POS_C} strokeWidth={1.8} />
        <text x={160} y={92} textAnchor="middle" fontSize="9" fill={POS_C} fontWeight="600">V_f</text>
        <circle cx={160} cy={96} r={12} fill="none" stroke={POS_C} strokeWidth={1.5} />
        <line x1={160} y1={108} x2={160} y2={115} stroke={DIM_C} strokeWidth={1.5} />
        {/* Z2, Z0 dimmed / open */}
        <Box x={30}  y={50} label="Z\u2082_Th" c={NEG_C} dim />
        <Box x={220} y={50} label="Z\u2080_Th" c={ZER_C} dim />
        <text x={30 + 35} y={80} textAnchor="middle" fontSize="7.5" fill={DIM_C}>not connected</text>
        <text x={220 + 35} y={80} textAnchor="middle" fontSize="7.5" fill={DIM_C}>not connected</text>
      </svg>
    );
  }

  if (seqDiagram === 'series') {
    // SLG: Z1 + Z2 + Z0 in series
    const xs = [30, 130, 230];
    const labels = ['Z\u2081_Th', 'Z\u2082_Th', 'Z\u2080_Th'];
    const colors = [POS_C, NEG_C, ZER_C];
    return (
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: '130px' }}>
        <FaultBus x={30 + 35} y={18} />
        {xs.map((x, i) => (
          <g key={i}>
            <Box x={x} y={35} label={labels[i]} c={colors[i]} />
            {i < 2 && <line x1={x + 70} y1={46} x2={xs[i + 1]} y2={46}
              stroke="#475569" strokeWidth={1.5} />}
          </g>
        ))}
        <line x1={65} y1={18} x2={65} y2={35} stroke={POS_C} strokeWidth={1.8} />
        <line x1={300} y1={57} x2={300} y2={72} stroke={ZER_C} strokeWidth={1.5} />
        <text x={300} y={86} textAnchor="middle" fontSize="9" fill={ZER_C} fontWeight="600">Vf</text>
        <circle cx={300} cy={90} r={11} fill="none" stroke={ZER_C} strokeWidth={1.5} />
        <line x1={300} y1={101} x2={300} y2={110} stroke={DIM_C} strokeWidth={1.5} />
        <text x={160} y={120} textAnchor="middle" fontSize="8" fill="#64748b" fontStyle="italic">Series connection: Z\u2081 + Z\u2082 + Z\u2080</text>
      </svg>
    );
  }

  if (seqDiagram === 'pos-neg-series') {
    // LL: Z1 + Z2 series; Z0 not used
    return (
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: '130px' }}>
        <FaultBus x={80} y={18} />
        <Box x={45} y={35} label="Z\u2081_Th" c={POS_C} />
        <line x1={80} y1={18} x2={80} y2={35} stroke={POS_C} strokeWidth={1.8} />
        <line x1={115} y1={46} x2={145} y2={46} stroke="#475569" strokeWidth={1.5} />
        <Box x={145} y={35} label="Z\u2082_Th" c={NEG_C} />
        <line x1={215} y1={57} x2={215} y2={72} stroke={NEG_C} strokeWidth={1.5} />
        <text x={215} y={86} textAnchor="middle" fontSize="9" fill={NEG_C} fontWeight="600">Vf</text>
        <circle cx={215} cy={90} r={11} fill="none" stroke={NEG_C} strokeWidth={1.5} />
        <line x1={215} y1={101} x2={215} y2={110} stroke={DIM_C} strokeWidth={1.5} />
        <Box x={225} y={50} label="Z\u2080_Th" c={ZER_C} dim />
        <text x={260} y={78} textAnchor="middle" fontSize="7.5" fill={DIM_C}>not used</text>
        <text x={160} y={120} textAnchor="middle" fontSize="8" fill="#64748b" fontStyle="italic">Series: Z\u2081 + Z\u2082 only; I\u2080 = 0</text>
      </svg>
    );
  }

  // DLG: Z2 || Z0 in parallel, then Z1 in series
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: '130px' }}>
      <FaultBus x={80} y={18} />
      <Box x={45} y={35} label="Z\u2081_Th" c={POS_C} />
      <line x1={80} y1={18} x2={80} y2={35} stroke={POS_C} strokeWidth={1.8} />
      <line x1={115} y1={46} x2={155} y2={46} stroke="#475569" strokeWidth={1.5} />
      {/* Junction */}
      <circle cx={155} cy={46} r={3} fill="#475569" />
      <line x1={155} y1={46} x2={155} y2={32} stroke={NEG_C} strokeWidth={1.5} />
      <Box x={120} y={22} w={70} h={18} label="Z\u2082_Th" c={NEG_C} />
      <line x1={155} y1={46} x2={155} y2={60} stroke={ZER_C} strokeWidth={1.5} />
      <Box x={120} y={52} w={70} h={18} label="Z\u2080_Th" c={ZER_C} />
      {/* Rejoin */}
      <line x1={190} y1={31} x2={225} y2={50} stroke="#475569" strokeWidth={1.5} />
      <line x1={190} y1={61} x2={225} y2={50} stroke="#475569" strokeWidth={1.5} />
      <circle cx={225} cy={50} r={3} fill="#475569" />
      <line x1={225} y1={50} x2={250} y2={50} stroke="#475569" strokeWidth={1.5} />
      <text x={160} y={88} textAnchor="middle" fontSize="8" fill="#64748b" fontStyle="italic">Z\u2081 series with (Z\u2082 \u2225 Z\u2080)</text>
    </svg>
  );
};

const SeqConnectionDiagram = () => {
  const [faultId, setFaultId] = useState('SLG');
  const ft = FAULT_TYPES.find((f) => f.id === faultId);

  return (
    <div data-testid="seq-connection-diagram" className="space-y-4">
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Each fault type requires a different sequence network connection at the fault point.
        Select a fault type to see how Z\u2081, Z\u2082, Z\u2080 are connected and which boundary conditions apply.
      </p>
      <div className="flex flex-wrap gap-2">
        {FAULT_TYPES.map((ft) => (
          <button
            key={ft.id}
            data-testid={`fault-type-${ft.id}`}
            onClick={() => setFaultId(ft.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors border ${
              faultId === ft.id
                ? 'text-white border-transparent'
                : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-teal-400'
            }`}
            style={faultId === ft.id ? { background: ft.color, borderColor: ft.color } : {}}
          >
            {ft.short}
          </button>
        ))}
      </div>

      {/* Diagram */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
        <ConnectionDiagram faultType={ft} />
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          { label: 'Fault Boundary Conditions', value: ft.boundary },
          { label: 'Network Connection',         value: ft.connection },
          { label: 'Sequence Current Formula',   value: ft.formula },
          { label: 'Fault Current Result',        value: ft.faultCurrent },
        ].map(({ label, value }) => (
          <div key={label} className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md p-3">
            <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wide mb-1">{label}</p>
            <p className="text-xs font-mono text-slate-800 dark:text-slate-200">{value}</p>
          </div>
        ))}
      </div>
      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-md p-3">
        <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
          <span className="font-semibold">Key insight: </span>{ft.keyFact}
        </p>
      </div>
    </div>
  );
};

export default SeqConnectionDiagram;
